import { Link } from 'react-router-dom';
import { siteInfo } from '@/mocks/site';
import { categories } from '@/mocks/categories';
import { featuredArticles, latestArticles } from '@/mocks/articles';
import StructuredData from '@/components/feature/StructuredData';

const homeStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: siteInfo.name,
  url: siteInfo.url,
  description: siteInfo.description,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${siteInfo.url}/search?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

const statItems = [
  { value: siteInfo.stats.resources, label: '收录资源', icon: 'ri-database-2-line' },
  { value: siteInfo.stats.categories, label: '资源分类', icon: 'ri-folder-2-line' },
  { value: siteInfo.stats.articles, label: '精选文章', icon: 'ri-article-line' },
  { value: siteInfo.stats.users, label: '服务用户', icon: 'ri-user-heart-line' },
];

export default function Home() {
  return (
    <>
      <StructuredData data={homeStructuredData} />

      <section className="relative w-full min-h-[520px] md:min-h-[620px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Grand%20modern%20academic%20library%20interior%20with%20tall%20wooden%20bookshelves%20reaching%20ceiling%2C%20warm%20golden%20reading%20lamps%2C%20scholarly%20atmosphere%2C%20spiral%20staircase%20in%20center%2C%20soft%20natural%20light%20streaming%20through%20large%20arched%20windows%2C%20elegant%20architectural%20details%2C%20rich%20wood%20tones%2C%20vaulted%20ceiling%2C%20knowledge%20repository%20concept&width=1800&height=1200&seq=hero-academic-01&orientation=landscape"
          alt="第一学术福利导航网 - 学术资源导航平台"
          className="absolute inset-0 w-full h-full object-cover object-top"
          title="第一学术福利导航网 - 免费学术资源一站式导航"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55"></div>

        <div className="relative z-10 w-full max-w-[900px] mx-auto px-4 md:px-6 text-center py-20">
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
            免费学术资源一站式导航
          </h1>
          <p className="text-base md:text-lg text-white/80 max-w-[680px] mx-auto leading-relaxed mb-8">
            {siteInfo.description}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/category/free-paper-db"
              className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              探索学术资源
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
            <Link
              to="/about"
              className="px-8 py-3 rounded-full bg-background-50/20 backdrop-blur-sm text-white font-medium text-sm hover:bg-background-50/30 transition-colors whitespace-nowrap cursor-pointer border border-white/20"
            >
              了解更多
            </Link>
          </div>
        </div>
      </section>

      <section className="relative z-20 -mt-12 mb-16 md:mb-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group bg-background-50 rounded-xl p-4 md:p-5 text-center hover:-translate-y-1 transition-all duration-300 cursor-pointer border border-background-200/70"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 mx-auto mb-3 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50 transition-colors">
                  <i className={`${cat.icon} text-xl md:text-2xl`}></i>
                </div>
                <h3 className="font-heading font-semibold text-sm text-foreground-900 mb-1 whitespace-nowrap">
                  {cat.name}
                </h3>
                <p className="text-xs text-foreground-400">{cat.resourceCount} 个资源</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mb-16 md:mb-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="flex items-end justify-between mb-8 md:mb-10">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                精选推荐
              </h2>
              <p className="text-sm text-foreground-500">编辑团队精挑细选的优质学术资源</p>
            </div>
            <Link
              to="/category/free-paper-db"
              className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {featuredArticles.slice(0, 6).map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    title={article.title}
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-background-50/90 text-primary-700">
                      {article.categoryName}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed mb-4">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-foreground-400">
                    <span>{article.publishedAt}</span>
                    <span>·</span>
                    <span>{article.readTime} 分钟阅读</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-100/70 py-16 md:py-20 mb-16 md:mb-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              最新文章
            </h2>
            <p className="text-sm text-foreground-500 max-w-[500px] mx-auto">
              持续更新的学术资源使用指南与工具推荐
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {latestArticles.slice(0, 4).map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group bg-background-50 rounded-xl p-5 border border-background-200/70 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
              >
                <span className="inline-block px-2 py-0.5 rounded-md text-xs font-medium bg-secondary-100 text-secondary-700 mb-3">
                  {article.categoryName}
                </span>
                <h3 className="font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                  {article.title}
                </h3>
                <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed mb-4">
                  {article.excerpt}
                </p>
                <div className="flex items-center gap-2 text-xs text-foreground-400">
                  <span>{article.publishedAt}</span>
                  <span>·</span>
                  <span>{article.readTime} 分钟</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mb-16 md:mb-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              我们的数据
            </h2>
            <p className="text-sm text-foreground-500">持续增长的学术资源生态</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-6">
            {statItems.map((stat) => (
              <div
                key={stat.label}
                className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8 text-center"
              >
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                  <i className={`${stat.icon} text-xl`}></i>
                </div>
                <div className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-foreground-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-secondary-100/70 py-16 md:py-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            开始你的学术探索之旅
          </h2>
          <p className="text-sm text-foreground-500 max-w-[500px] mx-auto mb-8">
            无论你是本科生、研究生还是科研工作者，第一学术福利导航网都能帮你更快找到所需资源
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/category/free-paper-db"
              className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              立即探索
            </Link>
            <Link
              to="/article/usage-guide"
              className="px-8 py-3 rounded-full bg-background-50 text-foreground-700 font-medium text-sm hover:bg-background-200/70 transition-colors border border-background-200/70 whitespace-nowrap cursor-pointer"
            >
              查看使用指南
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}